import type {
  TFriendAnalyticsCollectResult,
  TFriendAnalyticsDailyItem,
} from 'types/friendAnalytics'

/** 画面とCSVで表示する指標。ターゲットリーチの収集データは引き続き保持する。 */
export const FRIEND_ANALYTICS_VISIBLE_METRICS = ['followers', 'blocks'] as const

/** 期間プリセット（日数）。参考にした LINE公式アカウントマネージャーと同じ粒度。 */
export const FRIEND_ANALYTICS_RANGE_PRESETS = [7, 30, 90] as const

export type FriendAnalyticsRangePreset =
  (typeof FRIEND_ANALYTICS_RANGE_PRESETS)[number]

export type DateRange = { from: string; to: string }

export type DailySort = 'asc' | 'desc'

const MS_PER_DAY = 86_400_000

/**
 * 指定できる期間の最大日数。
 * backend の FRIEND_ANALYTICS_MAX_RANGE_DAYS と必ず同じ値にすること。
 */
export const FRIEND_ANALYTICS_MAX_RANGE_DAYS = 397

/** `YYYY-MM-DD` を UTC 00:00 のミリ秒へ。書式・実在性が不正なら null。 */
export const parseDateKey = (value: string): number | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const time = Date.parse(`${value}T00:00:00.000Z`)
  if (Number.isNaN(time)) return null
  // Date.parse は 2026-02-30 のような実在しない日を繰り上げるので往復で検証する
  return formatDateKey(time) === value ? time : null
}

/** UTC 00:00 のミリ秒を `YYYY-MM-DD` へ。 */
export const formatDateKey = (time: number): string =>
  new Date(time).toISOString().slice(0, 10)

/** `YYYY-MM-DD` を画面表示用の `YYYY/MM/DD` へ。不正な値はそのまま返す。 */
export const formatDisplayDate = (value: string): string =>
  parseDateKey(value) === null ? value : value.replace(/-/g, '/')

/**
 * 終了日を起点に、指定日数ぶん遡った期間を作る。
 *
 * @param endDate 終了日（通常は集計完了日）
 * @param days プリセットの日数。終了日を含む
 */
export const buildRangeFromPreset = (
  endDate: string,
  days: FriendAnalyticsRangePreset,
): DateRange | null => {
  const end = parseDateKey(endDate)
  if (end === null) return null
  return { from: formatDateKey(end - (days - 1) * MS_PER_DAY), to: endDate }
}

/** 期間の問題の種類。`friendAnalyticsStrings.errors` のキーと対応する。 */
export type RangeIssue = 'invalidRange' | 'rangeTooLong'

/** 終了日を含む日数。 */
const countRangeDays = (from: number, to: number): number =>
  Math.round((to - from) / MS_PER_DAY) + 1

/**
 * 期間の問題を返す。問題が無ければ null。
 *
 * 未入力（両方空）は「まだ決まっていない」だけなので問題として扱わない。
 * 上限日数を画面側でも見ることで、400が返ってから気づく往復を無くす。
 */
export const getRangeIssue = (range: DateRange): RangeIssue | null => {
  if (!range.from && !range.to) return null

  const from = parseDateKey(range.from)
  const to = parseDateKey(range.to)
  if (from === null || to === null || from > to) return 'invalidRange'
  if (countRangeDays(from, to) > FRIEND_ANALYTICS_MAX_RANGE_DAYS) {
    return 'rangeTooLong'
  }
  return null
}

/** 期間が有効か（読めて from <= to で、上限日数以内）。 */
export const isValidRange = (range: DateRange): boolean => {
  const from = parseDateKey(range.from)
  const to = parseDateKey(range.to)
  if (from === null || to === null || from > to) return false
  return countRangeDays(from, to) <= FRIEND_ANALYTICS_MAX_RANGE_DAYS
}

/** 増減の表示。0 は符号を付けず `(0)`、未取得は null。 */
export const formatDiff = (diff: number | null): string | null => {
  if (diff === null) return null
  if (diff === 0) return '(0)'
  return `(${diff > 0 ? '+' : '-'}${Math.abs(diff).toLocaleString('ja-JP')})`
}

/** 日付で並べ替える。日付は一意なので同値の考慮は不要。 */
export const sortDailyItems = (
  items: readonly TFriendAnalyticsDailyItem[],
  sort: DailySort,
): TFriendAnalyticsDailyItem[] =>
  [...items].sort((a, b) =>
    sort === 'asc'
      ? a.date.localeCompare(b.date)
      : b.date.localeCompare(a.date),
  )

/**
 * CSVのセルを RFC4180 でクオートし、数式として解釈され得る先頭文字を無害化する。
 *
 * backend の `escapeSurveyCsvCell` と同じ方針。
 */
export const escapeCsvCell = (value: string): string => {
  const injectionRisk = /^[=+\-@＝＋－＠]/.test(value)
  const normalized = injectionRisk ? `'${value}` : value
  return `"${normalized.replace(/"/g, '""')}"`
}

/**
 * 日次データを CSV 文字列にする。
 *
 * Excel が UTF-8 と認識できるよう BOM を付け、改行は CRLF にする。
 */
export const buildDailyCsv = (
  items: readonly TFriendAnalyticsDailyItem[],
  headers: readonly [string, string, string],
): string => {
  const rows = [
    headers.map(escapeCsvCell).join(','),
    ...items.map((item) =>
      [
        escapeCsvCell(item.date),
        ...FRIEND_ANALYTICS_VISIBLE_METRICS.map((key) => String(item[key])),
      ].join(','),
    ),
  ]
  return `﻿${rows.join('\r\n')}\r\n`
}

/** ダウンロードファイル名。期間が分かる形にする。 */
export const buildCsvFileName = (range: DateRange): string =>
  `friend-analytics_${range.from}_${range.to}.csv`

type CollectStrings = {
  completed: string
  partial: string
  busy: string
  upToDate: string
}

/**
 * 「LINEから最新データを取得」の結果を1行の文言にする。
 *
 * 取得後の欠損は backend の判定を使う。旧backendの応答では取得前の欠損しか
 * 分からないため、欠損があった場合は部分取得として再取得を案内する。
 */
export const buildCollectMessage = (
  result: TFriendAnalyticsCollectResult | undefined,
  strings: CollectStrings,
): string | null => {
  if (!result) return null
  if (result.skippedByLock) return strings.busy

  const days = String(result.savedDays)
  const remaining =
    result.hasRemaining ??
    (result.channelsWithMissingDays > 0 ||
      result.budgetExhausted ||
      result.failedChannels > 0 ||
      (result.statusCounts.unready ?? 0) > 0)
  if (remaining) return strings.partial.replace('$days', days)
  if (result.savedDays === 0) return strings.upToDate
  return strings.completed.replace('$days', days)
}
